'use client'

import {
  AspectRatio,
  Box,
  Flex,
  HStack,
  SimpleGrid,
  Text,
  VStack
} from '@chakra-ui/react'
import React from 'react'

import Image from 'next/image'
import { LocationIcon } from '@icons/location-icon'
import { Link as ScrollLink } from 'react-scroll'

import Link from 'next/link'
import { useResponsive } from '@common/hooks'

interface Outlets {
  location: string
  stores: string[]
}

const GuardianOutlets: Outlets[] = [
  {
    location: 'Guardian - Central',
    stores: [
      'Blk 704 Ang Mo Kio Ave 8 #01-2559C',
      'Blk 190 Toa Payoh Lorong 6 #01-578',
      'Ang Mo Kio Hub #B2-01/02',
      '100 Tras Street, # 02-09',
      'Clarke Quay Central #01-55/56/57'
    ]
  },
  {
    location: 'Guardian - North East',
    stores: ['Blk 744 Bedok Reservoir Road #01-3063', 'Century Square #B1-01']
  },
  {
    location: 'Guardian - North West',
    stores: ['Sun Plaza #B1-11', 'Blk 888 Woodlands Drive 50, #01-731/731A']
  },
  {
    location: 'Guardian - South East',
    stores: [
      'Blk 113 Aljuined Ave 2, #01-27',
      'Eastpoint Mall #02-01/02/35',
      '65 Airport Blvd North T3, B2-24',
      'Paya Lebar Quarter #B2-35',
      'Bedok Mall #B2-45/46/47'
    ]
  },
  {
    location: 'Guardian - South West',
    stores: [
      'Jurong Point #B1-27/28',
      'Blk 503 Jurong West Avenue 1, #01-845',
      'JEM #B1-16/17'
    ]
  }
]

const ShellOutlets: Outlets[] = [
  {
    location: 'Shell Select - Central',
    stores: [
      '3535 Ang Mo Kio Ave 6',
      '450 Telok Blangah Rd',
      '150 Bukit Timah Road',
      '1200 Upper Thomson Rd',
      '143 Bukit Timah Rd',
      '80 Upper Thomson Rd',
      '548 Havelock Rd',
      '298 Yio Chu Kang Rd',
      '259 MacPherson Rd'
    ]
  },
  {
    location: 'Shell Select - North East',
    stores: [
      '600 Hougang Ave 3',
      '61 Sengkang East Rd',
      '821 Punggol Rd',
      '9 Tampines Ave 2',
      '49 Serangoon Garden Way',
      '158 Yio Chu Kang Rd'
    ]
  },
  {
    location: 'Shell Select - North West',
    stores: ['314 Dunearn Road', '20 Woodlands Ave 9']
  },
  {
    location: 'Shell Select - South East',
    stores: ['110 Braddell Rd', '255 Paya Lebar Rd', '98 Paya Lebar Rd']
  },
  {
    location: 'Shell Select - South West',
    stores: [
      '11 Bukit Batok West Ave 3',
      '328 Pasir Panjang Rd',
      '311 Jln. Ahmad Ibrahim',
      '219 Upper Bukit Timah Road',
      '168 Pasir Panjang Rd'
    ]
  }
]

const CheersOutlets: Outlets[] = [
  {
    location: 'Cheers/Esso - Central',
    stores: [
      '2225 Ang Mo Kio Avenue 8',
      '262 Queensway',
      '91 Ang Mo Kio Ave 4 #B2-02/03/04',
      '622 Queensway',
      'Block 407 Ang Mo Kio Avenue 10, #01-741',
      'Rochor MRT Station #B1-02',
      '611 Aljunied Road',
      '150 Sengkang West Way',
      '751 Bukit Timah Road',
      '396 Telok Blangah Road',
      '141 Bukit Timah Road',
      '399 Toa Payoh Lorong 2',
      'Downtown MRT Station #B1-02',
      '353 Tanglin Road',
      '30 Dunearn Road',
      'The Central #01-05/06/14',
      '1 Eng Hoon Street, #01-02 (Hotel 81 - Osaka)',
      'Telok Ayer MRT Station #B1-02',
      '2991 Jalan Bukit Merah',
      '594 Upper Thomson Road',
      '1 Jervois Road ',
      '373 Upper Aljunied Road',
      '71 Kampong Bahru Road ',
      '553 Upper Thomson Road',
      '110 / 114 Macpherson Road ',
      '353 Upper Thomson Road',
      'Outram Community Hospital ',
      '299 Yio Chu Kang Road'
    ]
  },
  {
    location: 'Cheers/Esso - North East',
    stores: [
      '1 Hougang Street 91, #01-35',
      '211 Punggol Road',
      'Blk 810 Hougang Central #01-214',
      '40 Tampines Avenue 9',
      '384 Lorong Chuan',
      'Blk 866A Tampines St 83 #01-01',
      'Northshore Plaza II #02-05/06',
      'Tampines MRT Station #01-02',
      '61 Pasir Ris Drive 1',
      '866 Upper Serangoon Road'
    ]
  },
  {
    location: 'Cheers/Esso - North West',
    stores: [
      '15 Binjai Park',
      '593 Sembawang Road',
      'Canberra MRT #01-03',
      '10 Yishun Avenue 9',
      '650 Dunearn Road',
      '50 Woodlands Avenue 1',
      '30 Greenwood Avenue',
      'Block 548 Woodlands Drive 44 #01-06 Vista Point',
      '37 Jalan Jurong Kechil',
      'Woodlands Thomson East Coast Line #B1-28',
      '590 Sembawang Road',
      'Woodlands South MRT Station #B1-04/05/06'
    ]
  },
  {
    location: 'Cheers/Esso - South East',
    stores: [
      '65 Airport Boulevard T3 Departure, #02-98',
      'Block 12 Haig Road #01-333',
      '10 Airport Boulevard, T4 Arrival Hall, #01-09',
      '708 Mountbatten Road',
      '799 Bedok South Avenue 1',
      '10 Marine Vista #01-33, Neptune Court',
      '99 Bedok North Road',
      'Paya Lebar MRT #01-11/12',
      '649 Changi Road',
      '916 Sim Avenue #B1-70/71, Parc Esta',
      '239 / 255 East Coast Road',
      '1 Stadium Place #01-03, Sports Hub Retail Mall',
      '590 East Coast Road',
      '955 Upper Changi Road North',
      '225 Guillemard Road',
      '251 Upper East Coast Road',
      '638 Geylang Road Lorong 38'
    ]
  },
  {
    location: 'Cheers/Esso - South West',
    stores: [
      '813 Bukit Batok West Ave 5',
      '13 Keppel Bay View #B1-99',
      'Chinese Garden MRT Station #01-03',
      'No 2 Keppel Bay Drive #B1-133',
      '50 Choa Chu Kang Avenue 3',
      '1 Lower Kent Ridge Road #01-28',
      '31 Choa Chu Kang Way',
      '100 North Buona Vista Road #01-01',
      '50 Choa Chu Kang Drive',
      '19 Pioneer Road',
      'No.2 College Avenue West, #01-05',
      '242A Pasir Panjang Road',
      '150 Jurong East Avenue 1',
      '213 Upper Bukit Timah Road',
      '10 Jurong West Avenue 1',
      '181 West Coast Road',
      '302 Jalan Ahmad Ibrahim',
      '93 West Coast Vale #B1-03/04',
      '253 Jalan Buroh'
    ]
  }
]

const OutletsLocation = ({ outlet }: { outlet: Outlets }) => {
  return (
    <Box data-aos="fade-up" my={8}>
      <Text textStyle="gripExclusiveOfferTitle" w={'100%'}>
        {outlet.location}
      </Text>
      <SimpleGrid columns={{ base: 1, sm: 2 }} gap={6} w={'100%'} mt={5}>
        {outlet.stores.map((store) => {
          return (
            <HStack key={store}>
              <Box
                minW={{
                  base: '20px',
                  lg: '28px'
                }}
                w={{
                  base: '20px',
                  lg: '28px'
                }}
                h={{
                  base: '20px',
                  lg: '28px'
                }}
              >
                <LocationIcon width="100%" height="100%" />
              </Box>
              <Text textStyle="gripExclusiveOffer">{store}</Text>
            </HStack>
          )
        })}
      </SimpleGrid>
    </Box>
  )
}

const ExclusiveOffer = () => {
  const { smMin } = useResponsive()
  return (
    <Box maxW="100%" mx="auto" mt={12} mb={12}>
      <VStack data-aos="fade-up" alignItems={'center'} gap={12}>
        <Text textAlign={'center'} textStyle="gripProblemTitle">
          Exclusive Offer
        </Text>
        <AspectRatio
          width={{
            base: 250,
            sm: 320,
            lg: 400
          }}
          ratio={950 / 864}
          position="relative"
          zIndex={10}
          ml={{
            base: '-32px'
          }}
        >
          <Image
            alt="sale tumbler"
            src={
              'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/sale-tumbler-1713522234214.png'
            }
            fill
          />
        </AspectRatio>
        <Text textAlign={'center'} textStyle="gripProblemTitle">
          Participating outlets
        </Text>
      </VStack>
      <Box data-aos="fade-up" my={8}>
        <Text textStyle={'gripExclusiveOfferTitleLarge'} w={'100%'}>
          Online:
        </Text>
        <SimpleGrid
          columns={4}
          gap={{
            base: '10px',
            md: '40px',
            lg: '70px'
          }}
          w={'100%'}
          mt={5}
        >
          <AspectRatio
            ratio={1 / 1}
            position="relative"
            zIndex={10}
            cursor="pointer"
          >
            <Link
              href="https://habbit.sg/products/arctic-grip-tumbler?utm_source=microsite&utm_medium=button&utm_campaign=arcticgrip&utm_id=clicks"
              target="_blank"
            >
              <Image
                alt="habbit"
                src={
                  'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/habbit-logo-1713522234214.png'
                }
                fill
              />
            </Link>
          </AspectRatio>

          <AspectRatio
            ratio={1 / 1}
            position="relative"
            zIndex={10}
            cursor="pointer"
          >
            <Link
              href="https://www.jml.sg/sg/products/arctic-grip-tumbler-480ml?utm_source=microsite&utm_medium=button&utm_campaign=arcticgrip&utm_id=clicks"
              target="_blank"
            >
              <Image
                alt="jml"
                src={
                  'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/jml-logo-1713522234214.png'
                }
                fill
              />
            </Link>
          </AspectRatio>
          <AspectRatio
            ratio={1 / 1}
            position="relative"
            zIndex={10}
            cursor="pointer"
          >
            <Link
              href="https://shopee.sg/universal-link/product/236618592/22254205095/?deep_and_web=1&utm_campaign=s236618592_ss_sg_webs_arcticgripmicrosite&utm_source=website&utm_medium=seller&utm_content=&smtt=9"
              target="_blank"
            >
              <Image
                alt="shopee"
                src={
                  'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/shopee-logo-1713522234215.png'
                }
                fill
              />
            </Link>
          </AspectRatio>
          <AspectRatio
            ratio={1 / 1}
            position="relative"
            zIndex={10}
            cursor="pointer"
          >
            <Link
              href="https://c.lazada.sg/t/c.Yllw38?intent=false&fallback=true&url=https%3A%2F%2Fwww.lazada.sg%2Fproducts%2Fi2885081500.html"
              target="_blank"
            >
              <Image
                alt="lazada"
                src={
                  'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/lazada-logo-1713522234214.png'
                }
                fill
              />
            </Link>
          </AspectRatio>
        </SimpleGrid>
      </Box>
      <Box data-aos="fade-up" my={8} id="jml">
        <Text textStyle={'gripExclusiveOfferTitleLarge'} w={'100%'}>
          Retailers:
        </Text>
        <Flex
          direction={{ base: 'column', sm: 'row' }}
          justify="space-between" //
          align={{ base: 'center', sm: 'center' }}
          gap={6}
          w="100%"
          mt={5}
        >
          <Box
            flex={1}
            w={{
              base: '60%',
              sm: 'auto'
            }}
            paddingInline="6px"
          >
            <ScrollLink
              to="jml"
              smooth={true}
              duration={500}
              offset={smMin ? 10 : 450} // Adjusted for header height
            >
              <AspectRatio ratio={1366 / 768} cursor="pointer">
                <Image
                  src="https://habbit-sg.s3.ap-southeast-1.amazonaws.com/jml-1715194427238.png"
                  fill
                  alt="wonder"
                  style={{
                    borderRadius: '18px'
                  }}
                />
              </AspectRatio>
            </ScrollLink>
          </Box>
          <Box
            flex={1}
            w={{
              base: '60%',
              sm: 'auto'
            }}
            cursor="pointer"
          >
            <ScrollLink
              to="cheers"
              smooth={true}
              duration={500}
              offset={-150} // Adjusted for header height
            >
              <AspectRatio ratio={1366 / 768} position="relative">
                <Image
                  src={
                    'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/cheers-1713812201246.png'
                  }
                  alt="cheers"
                  fill
                />
              </AspectRatio>
            </ScrollLink>
          </Box>
          <Box
            flex={1}
            w={{
              base: '60%',
              sm: 'auto'
            }}
            cursor="pointer"
          >
            <ScrollLink
              to="shell-select"
              smooth={true}
              duration={500}
              offset={-150} // Adjusted for header height
            >
              <AspectRatio ratio={1366 / 768} position="relative">
                <Image
                  src={
                    'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/shell-select-1713812201250.png'
                  }
                  alt="shell select"
                  fill
                />
              </AspectRatio>
            </ScrollLink>
          </Box>
          <Box
            flex={1}
            w={{
              base: '60%',
              sm: 'auto'
            }}
            cursor="pointer"
          >
            <ScrollLink
              to="guardian"
              smooth={true}
              duration={500}
              offset={-150} // Adjusted for header height
            >
              <AspectRatio ratio={1366 / 768} position="relative">
                <Image
                  src={
                    'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/guardian-1713812201249.png'
                  }
                  alt="guardian"
                  fill
                />
              </AspectRatio>
            </ScrollLink>
          </Box>
        </Flex>
        <SimpleGrid columns={{ base: 1, sm: 2 }} gap={6} w={'100%'} mt={5}>
          <HStack flex={1}>
            <Box
              minW={{
                base: '20px',
                lg: '28px'
              }}
              w={{
                base: '20px',
                lg: '28px'
              }}
              h={{
                base: '20px',
                lg: '28px'
              }}
            >
              <LocationIcon width="100%" height="100%" />
            </Box>
            <Text textStyle="gripExclusiveOffer">
              JML @ Paya Lebar Square #01-24/25
            </Text>
          </HStack>
          <HStack flex={1}>
            <Box
              minW={{
                base: '20px',
                lg: '28px'
              }}
              w={{
                base: '20px',
                lg: '28px'
              }}
              h={{
                base: '20px',
                lg: '28px'
              }}
            >
              <LocationIcon width="100%" height="100%" />
            </Box>
            <Text textStyle="gripExclusiveOffer">
              JML @ Causeway Point #03-19
            </Text>
          </HStack>
        </SimpleGrid>
      </Box>
      <VStack
        data-aos="fade-up"
        mt={20}
        w={'100%'}
        alignItems={'start'}
        id="cheers"
      >
        <Text textAlign={'left'} textStyle={'oralcareParticipateTitle'}>
          CHEERS / FAIRPRICE EXPRESS (ESSO)
        </Text>
      </VStack>
      {CheersOutlets.map((outlet, i) => {
        return <OutletsLocation key={i} outlet={outlet} />
      })}
      <VStack
        data-aos="fade-up"
        mt={20}
        w={'100%'}
        alignItems={'start'}
        id="shell-select"
      >
        <Text textAlign={'left'} textStyle={'oralcareParticipateTitle'}>
          SHELL SELECT
        </Text>
      </VStack>
      {ShellOutlets.map((outlet, i) => {
        return <OutletsLocation key={i} outlet={outlet} />
      })}
      <VStack
        data-aos="fade-up"
        mt={20}
        w={'100%'}
        alignItems={'start'}
        id="guardian"
      >
        <Text textAlign={'left'} textStyle={'oralcareParticipateTitle'}>
          Guardian
        </Text>
      </VStack>
      {GuardianOutlets.map((outlet, i) => {
        return <OutletsLocation key={i} outlet={outlet} />
      })}
    </Box>
  )
}

export default ExclusiveOffer
